import { useCallback, useEffect, useRef } from 'react';

export const useEscalaCache = (terapeutaId) => {
  const cacheTimeoutRef = useRef(null);

  // Função para limpar cache específico por mês/ano
  const limparCacheEscala = useCallback((mes, ano) => {
    const cacheKey = `eventosEscalaTerap_${terapeutaId}_${ano}_${mes}`;
    localStorage.removeItem(cacheKey);
    console.log(`🗑️ Cache limpo para terapeuta ${terapeutaId}, mês ${mes}, ano ${ano}`);
  }, [terapeutaId]);

  // Função para limpar cache de um período específico
  const limparCachePeriodo = useCallback((dataInicio, dataFim) => {
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    
    const cacheKeys = Object.keys(localStorage).filter(key => 
      key.startsWith(`eventosEscalaTerap_${terapeutaId}_`)
    );
    
    cacheKeys.forEach(key => {
      // Extrair ano e mês da chave do cache
      const match = key.match(/eventosEscalaTerap_\d+_(\d+)_(\d+)/);
      if (match) {
        const ano = parseInt(match[1]);
        const mes = parseInt(match[2]);
        const dataCache = new Date(ano, mes - 1, 1); // mês é 0-based
        
        if (dataCache >= inicio && dataCache <= fim) {
          localStorage.removeItem(key);
          console.log(`🗑️ Cache limpo para período: ${key}`);
        }
      }
    });
  }, [terapeutaId]);

  // Função para limpar cache de um evento específico
  const limparCacheEvento = useCallback(async (eventoId) => {
    try {
      const response = await fetch(`/api/appointment/${eventoId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const evento = await response.json();
        const dataEvento = new Date(evento.startDatetime);
        const mes = dataEvento.getMonth() + 1;
        const ano = dataEvento.getFullYear();
        
        limparCacheEscala(mes, ano);
        
        // Limpar também o mês anterior e posterior para garantir
        const mesAnterior = mes === 1 ? 12 : mes - 1;
        const anoAnterior = mes === 1 ? ano - 1 : ano;
        const mesPosterior = mes === 12 ? 1 : mes + 1;
        const anoPosterior = mes === 12 ? ano + 1 : ano;
        
        limparCacheEscala(mesAnterior, anoAnterior);
        limparCacheEscala(mesPosterior, anoPosterior);
      }
    } catch (error) {
      console.error('Erro ao buscar evento para limpar cache:', error);
      // Se não conseguir buscar o evento, limpa todo o cache do terapeuta
      limparTodoCache();
    }
  }, [terapeutaId, limparCacheEscala]);

  // Função para limpar todo o cache do terapeuta
  const limparTodoCache = useCallback(() => {
    const cacheKeys = Object.keys(localStorage).filter(key => 
      key.startsWith(`eventosEscalaTerap_${terapeutaId}_`)
    );
    cacheKeys.forEach(key => localStorage.removeItem(key));
    console.log('🔄 Todo o cache da escala foi limpo');
  }, [terapeutaId]);

  // Função para invalidar cache quando há mudanças
  const invalidarCacheEscala = useCallback(() => {
    limparTodoCache();
    console.log('🔄 Cache da escala invalidado');
  }, [limparTodoCache]);

  // Função para marcar timestamp do cache
  const marcarTimestampCache = useCallback((mes, ano) => {
    localStorage.setItem(`cacheTimestamp_${terapeutaId}`, Date.now().toString());
  }, [terapeutaId]);

  // Função para verificar se o cache está válido
  const isCacheValido = useCallback((mes, ano) => {
    const cacheKey = `eventosEscalaTerap_${terapeutaId}_${ano}_${mes}`;
    const cache = localStorage.getItem(cacheKey);
    
    if (!cache) return false;
    
    try {
      JSON.parse(cache);
      
      // Verificar se o cache não está muito antigo
      const cacheTimestamp = localStorage.getItem(`cacheTimestamp_${terapeutaId}`);
      if (cacheTimestamp) {
        const tempoCache = Date.now() - parseInt(cacheTimestamp);
        return tempoCache <= 5 * 60 * 1000; // 5 minutos
      }
      
      return true;
    } catch {
      return false;
    }
  }, [terapeutaId]);

  // Função para obter eventos do cache
  const getEventosFromCache = useCallback((mes, ano) => {
    const cacheKey = `eventosEscalaTerap_${terapeutaId}_${ano}_${mes}`;
    const cache = localStorage.getItem(cacheKey);
    
    if (cache && isCacheValido(mes, ano)) {
      try {
        return JSON.parse(cache);
      } catch {
        localStorage.removeItem(cacheKey);
        return null;
      }
    }
    
    return null;
  }, [terapeutaId, isCacheValido]);

  // Função para salvar eventos no cache
  const salvarEventosNoCache = useCallback((mes, ano, eventos) => {
    const cacheKey = `eventosEscalaTerap_${terapeutaId}_${ano}_${mes}`;
    
    if (eventos && eventos.length > 0) {
      localStorage.setItem(cacheKey, JSON.stringify(eventos));
      marcarTimestampCache(mes, ano);
      console.log(`💾 Cache salvo para terapeuta ${terapeutaId}, mês ${mes}, ano ${ano}`);
    }
  }, [terapeutaId, marcarTimestampCache]);

  // Listener para mudanças no localStorage (outras abas/componentes)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Se houver mudanças relacionadas a eventos deste terapeuta
      if (e.key && e.key.includes('eventosEscalaTerap_') && e.key.includes(`_${terapeutaId}_`)) {
        console.log('📡 Detectada mudança no cache da escala, invalidando...');
        invalidarCacheEscala();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Também verificar periodicamente se há mudanças (fallback)
    const interval = setInterval(() => {
      const cacheKeys = Object.keys(localStorage).filter(key => 
        key.startsWith(`eventosEscalaTerap_${terapeutaId}_`)
      );
      
      if (cacheKeys.length > 0) {
        // Verificar se o cache não está muito antigo (mais de 5 minutos)
        const cacheTimestamp = localStorage.getItem(`cacheTimestamp_${terapeutaId}`);
        if (cacheTimestamp) {
          const tempoCache = Date.now() - parseInt(cacheTimestamp);
          if (tempoCache > 5 * 60 * 1000) { // 5 minutos
            console.log('⏰ Cache expirado, invalidando...');
            invalidarCacheEscala();
          }
        }
      }
    }, 30000); // Verificar a cada 30 segundos

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [terapeutaId, invalidarCacheEscala]);

  return {
    limparCacheEscala,
    limparCachePeriodo,
    limparCacheEvento,
    limparTodoCache,
    invalidarCacheEscala,
    marcarTimestampCache,
    isCacheValido,
    getEventosFromCache,
    salvarEventosNoCache
  };
}; 